import {PhoneHQGlyph} from 'ch3-ui-lib';
import { useCallback, useEffect, useRef, useState } from "react";
import { getEncryptedMediaCacheKey } from "../../utils/encryptedMedia";
import type { MatrixEncryptedFile } from "../../utils/encryptedMedia";
import { getMatrixMediaBlob, getMatrixMediaSource } from "../../utils/matrixMedia";

interface AuthenticatedFileProps {
    src: string;
    filename?: string;
    mimeType?: string;
    size?: number;
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
    onDownloadStart?: () => void;
    onDownloadComplete?: () => void;
    onDownloadError?: (error: string) => void;
    encryptedFile?: MatrixEncryptedFile;
}

export const AuthenticatedFile = ({
                                      src,
                                      filename,
                                      mimeType,
                                      size,
                                      className,
                                      style,
                                      children,
                                       onDownloadStart,
                                       onDownloadComplete,
                                       onDownloadError,
                                       encryptedFile,
                                   }: AuthenticatedFileProps) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const objectUrlsRef = useRef<Set<string>>(new Set());
    const prepareDownloadPromiseRef = useRef<Promise<string> | null>(null);
    const encryptedMediaKey = getEncryptedMediaCacheKey(encryptedFile, mimeType);
    const encryptedFileRef = useRef(encryptedFile);

    useEffect(() => {
        encryptedFileRef.current = encryptedFile;
    }, [encryptedMediaKey, encryptedFile]);

    const formatFileSize = useCallback((bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const getFileIcon = useCallback((mimeType?: string): string => {
        if (!mimeType) return 'attachment';

        const type = mimeType.toLowerCase();
        if (type.includes('image')) return 'image';
        if (type.includes('video')) return 'movie';
        if (type.includes('audio')) return 'audiotrack';
        if (type.includes('pdf')) return 'picture_as_pdf';
        if (type.includes('text') || type.includes('json') || type.includes('xml')) return 'description';
        if (type.includes('zip') || type.includes('rar') || type.includes('tar') || type.includes('gz')) return 'folder_zip';
        if (type.includes('word') || type.includes('doc')) return 'description';
        if (type.includes('excel') || type.includes('sheet')) return 'table_chart';
        if (type.includes('powerpoint') || type.includes('presentation')) return 'slideshow';

        return 'attachment';
    }, []);

    const revokeObjectUrls = useCallback(() => {
        for (const objectUrl of objectUrlsRef.current) {
            URL.revokeObjectURL(objectUrl);
        }
        objectUrlsRef.current.clear();
    }, []);

    useEffect(() => {
        setDownloadUrl('');
        prepareDownloadPromiseRef.current = null;
        return revokeObjectUrls;
    }, [src, encryptedMediaKey, mimeType, revokeObjectUrls]);

    const prepareDownload = useCallback(async () => {
        if (prepareDownloadPromiseRef.current) {
            return prepareDownloadPromiseRef.current;
        }

        prepareDownloadPromiseRef.current = (async () => {
            try {
                setError(null);

                const currentEncryptedFile = encryptedFileRef.current;
                const mediaMxc = getMatrixMediaSource(src, currentEncryptedFile);

                if (!mediaMxc.startsWith('mxc://')) {
                    setDownloadUrl(mediaMxc);
                    return mediaMxc;
                }

                const blob = await getMatrixMediaBlob(src, currentEncryptedFile, mimeType);
                const objectUrl = URL.createObjectURL(blob);
                objectUrlsRef.current.add(objectUrl);
                setDownloadUrl(objectUrl);
                return objectUrl;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to prepare download';
                setError(errorMessage);
                onDownloadError?.(errorMessage);
                throw err;
            } finally {
                prepareDownloadPromiseRef.current = null;
            }
        })();

        return prepareDownloadPromiseRef.current;
    }, [src, mimeType, onDownloadError]);

    const handleDownload = useCallback(async () => {
        if (isDownloading) return;

        try {
            setIsDownloading(true);
            onDownloadStart?.();

            let urlToDownload = downloadUrl;
            if (!urlToDownload) {
                urlToDownload = await prepareDownload();
            }

            const link = document.createElement('a');
            link.href = urlToDownload;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            onDownloadComplete?.();
        } catch (err) {
            console.error('Failed to download file:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to download file';
            setError(errorMessage);
            onDownloadError?.(errorMessage);
        } finally {
            setIsDownloading(false);
        }
    }, [isDownloading, downloadUrl, prepareDownload, filename, onDownloadStart, onDownloadComplete, onDownloadError]);

    if (children) {
        return (
            <div onClick={handleDownload} className={className} style={style}>
                {children}
            </div>
        );
    }

    return (
        <div className={`bg-canvas border border-line rounded-lg p-3 hover:bg-surface-muted transition-colors ${className}`} style={style}>
            <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                    <PhoneHQGlyph name={isDownloading ? 'download' : getFileIcon(mimeType)} size={24} className={`text-ink-muted ${isDownloading ? 'animate-pulse' : ''}`}/>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate whitespace-pre-wrap message-content max-w-sm">
                        {filename || 'Unknown file'}
                    </p>
                    {size && (
                        <p className="text-sm text-ink-muted">
                            {formatFileSize(size)}
                        </p>
                    )}
                    {mimeType && (
                        <p className="text-xs text-ink-muted uppercase">
                            {mimeType.split('/')[1] || mimeType}
                        </p>
                    )}
                    {error && (
                        <p className="text-xs text-red-500 mt-1">{error}</p>
                    )}
                </div>
                <button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className={`flex-shrink-0 p-2 rounded transition-colors ${
                        isDownloading
                            ? 'text-ink-muted cursor-not-allowed'
                            : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50 cursor-pointer'
                    }`}
                    data-tooltip={isDownloading ? 'Downloading...' : 'Download file'}
                >
                    <PhoneHQGlyph name={isDownloading ? 'hourglass_empty' : 'download'} size={24}/>
                </button>
            </div>
        </div>
    );
};
